/**
 * Demo mode only (?mock=1): a believable diff, event list, cost and message flow for any mock
 * session, built deterministically from the session's own fields. Never used against a hub.
 */
import { create } from 'zustand';
import { diffTotals, parseUnifiedDiff, untrackedFile } from '@orgus/protocol';
import type { CostsState, DiffResult, DiffScope, Session, SessionClaim, SessionEvent, SessionEventsPage, SessionInputStatus, SessionMessage, SessionUsage } from '@orgus/protocol';

const MIN = 60_000;

const PATCH = `diff --git a/src/features/sessions/SessionDrawer.tsx b/src/features/sessions/SessionDrawer.tsx
index 3b18e51..a1c2d3e 100644
--- a/src/features/sessions/SessionDrawer.tsx
+++ b/src/features/sessions/SessionDrawer.tsx
@@ -12,9 +12,14 @@ import { Transcript } from './Transcript.tsx';
 import { useSessionParam } from './useSessionParam.ts';

-/** The session detail drawer. */
-export function SessionDrawerHost({ order = [] }: { order?: string[] }) {
-  const [id, setId] = useSessionParam();
+/** The session detail drawer, with tabs for the transcript, diff, events and cost. */
+export function SessionDrawerHost({ order = [] }: { order?: string[] }) {
+  const [id, setId] = useSessionParam();
+  const [tab, setTab] = useDrawerTab();
   const s = useSession(id);
+  // Wider for side-by-side diffs.
+  const width = tab === 'diff' ? 960 : 680;
   const { phase } = useConnection();
   const project = useProject(s?.projectId);
   const checkout = useCheckout(s?.checkoutId);
@@ -88,7 +93,7 @@ function DrawerBody({ s, machineName }: { s: Session; machineName: string }) {
       <Section title="Timeline" aside={live ? 'Live' : 'Ended'}>
-        <Transcript session={s} />
+        <Transcript session={s} limit={400} />
       </Section>
     </>
   );
diff --git a/src/lib/retry.ts b/src/lib/retry.ts
index 1111111..2222222 100644
--- a/src/lib/retry.ts
+++ b/src/lib/retry.ts
@@ -1,11 +1,13 @@
 export interface RetryOptions {
   attempts: number;
   /** First delay in ms; doubles each time. */
   baseMs: number;
+  /** Stop early when this returns false. */
+  retryIf?: (e: unknown) => boolean;
 }

 export async function retry<T>(fn: () => Promise<T>, o: RetryOptions): Promise<T> {
   let last: unknown;
   for (let i = 0; i < o.attempts; i++) {
     try {
       return await fn();
     } catch (e) {
       last = e;
-      await sleep(o.baseMs * 2 ** i);
+      if (o.retryIf && !o.retryIf(e)) throw e;
+      await sleep(o.baseMs * 2 ** i); // back off: 1x, 2x, 4x
     }
   }
diff --git a/docs/old-notes.md b/docs/notes.md
similarity index 88%
rename from docs/old-notes.md
rename to docs/notes.md
index 4444444..5555555 100644
--- a/docs/old-notes.md
+++ b/docs/notes.md
@@ -3,3 +3,3 @@
 The drawer opens with the session's transcript.
-Diffs are not shown yet.
+Diffs show the session's uncommitted work and its branch.
 Events list every tool call.
diff --git a/public/logo.png b/public/logo.png
index 6666666..7777777 100644
Binary files a/public/logo.png and b/public/logo.png differ
`;

function seeded(id: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return () => {
    h = Math.imul(h ^ (h >>> 15), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return ((h ^= h >>> 16) >>> 0) / 4294967296;
  };
}

export function demoDiff(s: Session, scope: DiffScope, commit?: string): DiffResult {
  const files = parseUnifiedDiff(PATCH);
  const commits = [
    { sha: 'a1b2c3d4e5f60718293a4b5c6d7e8f9012345678', short: 'a1b2c3d', subject: 'Drawer: tabs for transcript, diff, events and cost', author: 'Claude', at: s.lastActivity - 9 * MIN },
    { sha: 'b2c3d4e5f60718293a4b5c6d7e8f901234567890', short: 'b2c3d4e', subject: 'Retry: stop early when the error is not retryable', author: 'Claude', at: s.lastActivity - 31 * MIN },
  ];
  const base = { ref: 'origin/main', sha: '0f9e8d7c6b5a4f3e2d1c0b9a8f7e6d5c4b3a2918' };
  let shown = files;
  if (scope === 'working') shown = [files[0]!, untrackedFile('src/features/session-detail/notes.md', 'Open questions\n- Should replay show diffs inline?\n')];
  if (scope === 'commit') shown = commit?.startsWith('b2') ? [files[1]!] : [files[0]!, files[2]!, files[3]!];
  const commitMeta = scope === 'commit' ? commits.find((c) => c.sha.startsWith(commit ?? '')) : undefined;
  return {
    sessionId: s.id,
    scope,
    ...(commitMeta ? { commit: commitMeta } : {}),
    cwd: s.cwd,
    root: s.cwd,
    branch: 'feature/session-drawer',
    head: commits[0]!.sha,
    base,
    commits,
    commitsOmitted: 0,
    files: shown,
    totals: diffTotals(shown),
    truncated: false,
    at: Date.now(),
    tookMs: 42,
  };
}

const TOOLS: [string, string, string][] = [
  ['Read', 'SessionDrawer.tsx', 'apps/web/src/features/sessions/SessionDrawer.tsx'],
  ['Grep', 'useSessionParam', 'useSessionParam in apps/web/src'],
  ['Bash', 'Run the web tests', 'pnpm --filter @orgus/web test'],
  ['Edit', 'SessionDrawer.tsx', 'apps/web/src/features/sessions/SessionDrawer.tsx'],
  ['Glob', 'src/**/*.test.ts', 'src/**/*.test.ts'],
  ['Bash', 'Typecheck the workspace', 'pnpm typecheck'],
  ['Write', 'model.ts', 'apps/web/src/features/session-detail/model.ts'],
  ['WebSearch', 'radix tabs keyboard', 'radix tabs keyboard'],
];

export function demoEvents(s: Session): SessionEventsPage {
  const rand = seeded(s.id);
  const start = s.startedAt;
  const span = Math.max(60_000, s.lastActivity - start);
  const ev: SessionEvent[] = [];
  let seq = 0;
  const add = (frac: number, e: Omit<SessionEvent, 'seq' | 'at'>) => ev.push({ seq: ++seq, at: Math.round(start + frac * span), ...e });
  add(0, { kind: 'prompt', title: s.title });
  const steps = 18;
  for (let i = 0; i < steps; i++) {
    const f = (i + 1) / (steps + 3);
    if (i % 5 === 0) add(f, { kind: 'reply', title: ['Reading how the drawer works before changing it.', 'Found it. Making the change in one place.', 'Tests pass. Checking the edge cases next.', 'That works. Tidying up.'][(i / 5) % 4]! });
    const [tool, hint, detail] = TOOLS[Math.floor(rand() * TOOLS.length)]!;
    const failed = tool === 'Bash' && rand() < 0.25;
    const edit = tool === 'Edit' || tool === 'Write' ? { file: detail, before: tool === 'Edit' ? 'const width = 680;' : '', after: tool === 'Edit' ? "const width = tab === 'diff' ? 960 : 680;" : 'export type DrawerTab = ...;' } : undefined;
    add(f + 0.005, { kind: 'tool', tool, title: `${tool}: ${hint}`, detail, status: failed ? 'error' : 'ok', durationMs: Math.round(400 + rand() * 9000), ...(edit ? { edit } : {}) });
    if (i === 6) add(f + 0.01, { kind: 'subagent', tool: 'Task', title: 'Read the transcript formats in reference', meta: 'Explore', status: 'ok', durationMs: 4 * MIN, detail: 'Find how tool results and compactions are written.' });
    if (i === 9) add(f + 0.01, { kind: 'approval', tool: 'Bash', title: 'Refused: Bash (git push origin main)', status: 'denied', detail: 'Not yet, land it through the queue.' });
    if (i === 12) add(f + 0.01, { kind: 'compaction', title: 'Context compacted automatically', detail: '168k tokens of context before it' });
  }
  if (s.turns > 3) add(0.62, { kind: 'prompt', title: 'Keep going, and run the checks before you finish.' });
  if (s.lastText) add(1, { kind: 'reply', title: s.lastText });
  ev.sort((a, b) => (a.at ?? 0) - (b.at ?? 0));
  return { sessionId: s.id, events: ev, partial: false, omitted: 0, last: seq, at: Date.now() };
}

// ---------------------------------------------------------------------------
// Demo claims and messages
// ---------------------------------------------------------------------------

interface DemoControl {
  claims: Record<string, SessionClaim>;
  messages: Record<string, SessionMessage[]>;
  closed: Record<string, boolean>;
}

export const useDemoControl = create<DemoControl>()(() => ({ claims: {}, messages: {}, closed: {} }));

let n = 0;
export function demoClaim(s: Session, note?: string): SessionClaim {
  const c: SessionClaim = { sessionId: s.id, machineId: s.machineId, by: 'owner', at: Date.now(), auditId: `au_demo_${++n}`, ...(note ? { note } : {}) };
  useDemoControl.setState((st) => ({ claims: { ...st.claims, [s.id]: c } }));
  return c;
}

export function demoRelease(s: Session): void {
  useDemoControl.setState((st) => {
    const claims = { ...st.claims };
    delete claims[s.id];
    return { claims };
  });
}

export function demoInput(s: Session): SessionInputStatus {
  if (s.origin !== 'orgus') return { sessionId: s.id, accepting: false, reason: 'Orgus did not launch this session, so it cannot send it messages.' };
  if (s.status === 'ended') return { sessionId: s.id, accepting: false, reason: 'The session has ended.' };
  if (useDemoControl.getState().closed[s.id]) return { sessionId: s.id, accepting: false, reason: 'Its input was closed from Orgus, so it finishes what it is doing and exits.' };
  return { sessionId: s.id, accepting: true, busy: s.status === 'working', closesAt: s.status === 'working' ? null : Date.now() + 24 * MIN };
}

export function demoSend(s: Session, text: string): SessionMessage {
  const m: SessionMessage = { id: `msg_demo_${++n}`, sessionId: s.id, machineId: s.machineId, text, by: 'owner', at: Date.now(), state: 'sent' };
  const put = (x: SessionMessage) =>
    useDemoControl.setState((st) => {
      const list = (st.messages[s.id] ?? []).filter((y) => y.id !== x.id);
      return { messages: { ...st.messages, [s.id]: [...list, x] } };
    });
  put(m);
  setTimeout(() => put({ ...m, state: 'delivered', deliveredAt: Date.now() }), 900);
  return m;
}

export function demoCloseInput(s: Session): SessionInputStatus {
  useDemoControl.setState((st) => ({ closed: { ...st.closed, [s.id]: true } }));
  return demoInput(s);
}

/** A seeded message history so the composer is not empty in screenshots. */
export function seedDemoMessages(sessions: Session[]): void {
  const s = sessions.find((x) => x.origin === 'orgus' && x.status !== 'ended');
  if (!s || useDemoControl.getState().messages[s.id]) return;
  const at = s.lastActivity - 14 * MIN;
  useDemoControl.setState((st) => ({
    messages: {
      ...st.messages,
      [s.id]: [{ id: 'msg_demo_seed', sessionId: s.id, machineId: s.machineId, text: 'Also keep the j and k shortcuts working inside the new tabs.', by: 'owner', at, state: 'delivered', deliveredAt: at + 1200 }],
    },
  }));
}
